/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionPanel, Button } from '@coursera/cds-core';
import type { AccordionProps } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import AccordionHeader from 'bundles/unified-description-page-common/components/accordions/AccordionHeader';
import MultiActionAccordionHeader from 'bundles/unified-description-page-common/components/accordions/MultiActionAccordionHeader';
import type { CourseItemContentElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContent';
import type { CourseItemSubtitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitle';
import type { SyllabusItemTitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import SyllabusItemTitle from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';

const styles = {
  accordion: (hasDivider: boolean) => css`
    border-top: ${hasDivider ? '2px' : '0px'} solid var(--cds-color-grey-50);
    padding-top: ${hasDivider ? 'var(--cds-spacing-100)' : '0px'};
  `,
  panel: css`
    margin: 0;
  `,
  button: css`
    justify-content: space-between;
    text-align: left;
    border-radius: 0;

    /* hide the focus ring that overlaps adjacent buttons */
    overflow: hidden;

    :hover,
    :focus {
      background: var(--cds-color-blue-25);
      text-decoration: none;
    }
  `,
  mobileSyllabusItem: css`
    & + & {
      border-top: 2px solid var(--cds-color-grey-50);
    }
  `,
  headerContent: css`
    display: flex;
    gap: var(--cds-spacing-200);
    min-width: 0;
  `,
  titleContent: css`
    min-width: 0;
  `,
};

type Props = {
  children: [SyllabusItemTitleElement, CourseItemSubtitleElement, CourseItemContentElement];
  toggleButtonLabel: string;
  toggleButtonAriaLabel?: string;
  itemIndex: number;
  multiActionHeader?: boolean;
  trackingName?: string;
  thumbnail?: React.ReactNode;
};

function SyllabusItem({
  children,
  toggleButtonLabel,
  toggleButtonAriaLabel,
  itemIndex,
  multiActionHeader,
  trackingName,
  thumbnail,
}: Props): React.ReactElement<AccordionProps | 'div'> {
  const isMobile = useIsMobile();
  const { isModalOpen, setModalOpen } = useModal();

  const [title, subtitle, panel] = React.Children.toArray(children) as [
    SyllabusItemTitleElement,
    CourseItemSubtitleElement,
    CourseItemContentElement
  ];

  if (isMobile) {
    return (
      <div css={styles.mobileSyllabusItem}>
        <Button
          variant="ghost"
          icon={<ChevronNextIcon size="medium" />}
          css={styles.button}
          fullWidth
          onClick={setModalOpen}
        >
          <div css={styles.headerContent}>
            {thumbnail}
            <div>
              <SyllabusItemTitle {...title.props} disableLink={true} />
              {subtitle}
            </div>
          </div>
        </Button>
        <Modal open={isModalOpen} setOpen={setModalOpen} header={title} hideActions>
          {panel}
        </Modal>
      </div>
    );
  }

  const Header = multiActionHeader ? MultiActionAccordionHeader : AccordionHeader;

  return (
    <Accordion variant="silent" css={styles.accordion(itemIndex > 1)}>
      <Header
        toggleButtonLabel={toggleButtonLabel}
        toggleButtonAriaLabel={toggleButtonAriaLabel}
        trackingName={trackingName}
        labelProps={{
          'aria-label': title?.props?.title,
        }}
      >
        <div css={styles.headerContent}>
          {thumbnail}
          <div css={styles.titleContent}>
            {title}
            {subtitle}
          </div>
        </div>
      </Header>
      <AccordionPanel css={styles.panel}>{panel}</AccordionPanel>
    </Accordion>
  );
}

export default SyllabusItem;
